import React, { useState } from 'react';
import { Todo, TodoNavigationCalendarItem, TodoNavigationSummary } from '../types/data';
import { getTodoBadgeStatusFromTodos } from '../data/todoData';
import TodoMenuCalendarDate from './TodoMenuCalendarDate';
import IconButton from './IconButton';
import { createTokyoDateTime, getTokyoDateParts } from '../utils/dateUtils';
import './TodoMenuCalendar.css';

interface TodoMenuCalendarProps {
  selectedDate: string | null;
  onDateSelect: (date: string) => void;
  todos: Todo[];
  navigationSummary?: TodoNavigationSummary | null;
  currentMonth?: Date; // URL変更時に表示月を制御するプロパティ
  onCurrentMonthChange?: (date: Date) => void;
}

const TodoMenuCalendar: React.FC<TodoMenuCalendarProps> = ({ selectedDate, onDateSelect, todos, navigationSummary, currentMonth, onCurrentMonthChange }) => {
  const toCalendarDate = (date: Date) => {
    const parts = getTokyoDateParts(date);
    return new Date(parts.year, parts.month - 1, parts.day);
  };
  const [currentDate, setCurrentDate] = useState(() => toCalendarDate(new Date()));
  const summaryByDate = React.useMemo(() => {
    return new Map<string, TodoNavigationCalendarItem>((navigationSummary?.calendar ?? []).map(item => [item.date, item]));
  }, [navigationSummary]);

  // currentMonthが指定された場合、表示月を更新
  React.useEffect(() => {
    if (currentMonth) {
      setCurrentDate(toCalendarDate(currentMonth));
    }
  }, [currentMonth]);

  const formatDate = (date: Date): string => {
    // 時差問題を避けるため、現地時間ベースで日付文字列を生成
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const firstDayOfWeek = firstDay.getDay();
  
  const calendarDays = [];
  
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const prevDate = new Date(year, month, -i);
    calendarDays.push({
      date: prevDate,
      isCurrentMonth: false,
      dateString: formatDate(prevDate)
    });
  }
  
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day);
    calendarDays.push({
      date: date,
      isCurrentMonth: true,
      dateString: formatDate(date)
    });
  }
  
  const remainingDays = 42 - calendarDays.length;
  for (let day = 1; day <= remainingDays; day++) {
    const nextDate = new Date(year, month + 1, day);
    calendarDays.push({
      date: nextDate,
      isCurrentMonth: false,
      dateString: formatDate(nextDate)
    });
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(currentDate.getMonth() - 1);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
    onCurrentMonthChange?.(createTokyoDateTime({
      year: newDate.getFullYear(),
      month: newDate.getMonth() + 1,
      day: newDate.getDate(),
    }));
  };

  const monthNames = [
    '1月', '2月', '3月', '4月', '5月', '6月',
    '7月', '8月', '9月', '10月', '11月', '12月'
  ];

  const dayNames = ['日', '月', '火', '水', '木', '金', '土'];

  const isToday = (date: Date): boolean => {
    const today = toCalendarDate(new Date());
    return date.toDateString() === today.toDateString();
  };

  const getDateType = (day: any): "This Month" | "Other Month" | "Today" | "Selected" => {
    if (day.dateString === selectedDate) return 'Selected';
    if (isToday(day.date)) return 'Today';
    if (!day.isCurrentMonth) return 'Other Month';
    return 'This Month';
  };

  // 指定した日付のTODOバッジ状態を取得（共有ヘルパーを使用）
  const getTodoBadgeStatus = (date: string) => {
    const summaryItem = summaryByDate.get(date);
    if (summaryItem) {
      return {
        hasTodo: summaryItem.has_todo,
        hasExpiredTodo: summaryItem.has_expired_todo,
      };
    }
    if (navigationSummary) {
      return { hasTodo: false, hasExpiredTodo: false };
    }
    return getTodoBadgeStatusFromTodos(date, todos);
  };

  const getTodoBadgeStatusForDate = (dateString: string, isSelected: boolean): "hasTodo" | "hasExpiredTodo" | "hasTodoOnSelected" | null => {
    const badgeStatus = getTodoBadgeStatus(dateString);
    
    // Todoがない場合はバッジ表示なし
    if (!badgeStatus.hasTodo) {
      return null;
    }
    
    // 1. 期限切れかつ未完了のTodoがある場合は、Selected時でもhasExpiredTodo
    if (badgeStatus.hasExpiredTodo) {
      return "hasExpiredTodo";  // Surface/Caution
    }
    
    // 2. 期限切れではないTodoがある場合
    if (isSelected) {
      return "hasTodoOnSelected";  // Surface/Default
    }
    
    return "hasTodo";  // Surface/Middle
  };

  return (
    <div className="calendar">
      <div className="calendar-container">
        <div className="calendar-header">
          <IconButton 
            icon="chevronLeft"
            type="ghost"
            onClick={() => navigateMonth('prev')}
            aria-label="前の月"
            tooltip="前の月"
            tooltipPosition="top-right"
          />
          <h2 className="month-year text-heading-large">
            {year}年{monthNames[month]}
          </h2>
          <IconButton 
            icon="chevronRight"
            type="ghost"
            onClick={() => navigateMonth('next')}
            aria-label="次の月"
            tooltip="次の月"
            tooltipPosition="top-right"
          />
        </div>

        <div className="calendar-body">
          <div className="week-header">
            {dayNames.map(day => (
              <div key={day} className="day-header text-single-large">
                {day}
              </div>
            ))}
          </div>
          
          <div className="calendar-grid">
            {calendarDays.map((day, index) => (
              <div key={index} className="calendar-date-wrapper">
                <TodoMenuCalendarDate
                  date={day.date.getDate().toString()}
                  todoBadgeStatus={getTodoBadgeStatusForDate(day.dateString, day.dateString === selectedDate)}
                  type={getDateType(day)}
                  state="Default"
                  onClick={() => onDateSelect(day.dateString)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodoMenuCalendar;
